import {
  MaterialIcon,
  MaterialIconPropertiesType
} from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { Button } from "@website/ui/button";
import { CollapsibleText } from "@website/ui/collapsible-text";
import { Modal } from "@website/ui/modal";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import {
  FacilitiesItem,
  ReserveFacilitiesPropertiesTypes
} from "@website/widget/reserve-facilities/reserve-facilities.types";
import debounce from "lodash-es/debounce";
import upperFirst from "lodash-es/upperFirst";
import { useRouter } from "next/router";
import { memo, useCallback, useRef, useState } from "react";

const Component = ({
  facilitiesGroup,
  hotelType,
  hotelTitle,
  facilities,
  onModalScrollChange
}: ReserveFacilitiesPropertiesTypes) => {
  const router = useRouter();
  const reference = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const throttledModalScrollHandler = useRef(
    debounce((value: number) => {
      if (onModalScrollChange) {
        onModalScrollChange(Number(Math.min(100, Math.max(0, value))));
      }
    }, 1000)
  ).current;

  const getElements = useCallback(
    (facilities: Array<FacilitiesItem>) =>
      facilities?.map((facility) => (
        <div
          className="flex flex-row items-center gap-4 py-2"
          key={facility.title}
        >
          <MaterialIcon
            className="text-xl"
            name={
              (facility.icon as MaterialIconPropertiesType["name"]) ||
              "check_box"
            }
            variant="outlined"
          />
          <span className="text-black opacity-70">
            {upperFirst(
              numberConvert(facility.title, { locale: router.locale })
            )}
          </span>
        </div>
      )),
    []
  );

  const onScroll = useCallback(() => {
    const element = reference.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const value = (scrollTop / (scrollHeight - clientHeight)) * 100;

    throttledModalScrollHandler(value);
  }, [throttledModalScrollHandler]);

  if (!facilities?.length) return null;

  return (
    <div
      className="flex md:max-w-[720px] md:flex-row md:flex-wrap xl:max-w-[1200px]"
      id="hotel_facilities"
    >
      <CollapsibleText
        ariaLabel="facilities"
        buttonClassName="border-on-surface-high-emphasis text-button h-[40px] w-full rounded-[2rem] border py-2 md:w-[154px] flex justify-center !text-black mt-2"
        className="text-subtitle-2 mt-2 grid gap-x-5 md:grid-cols-2 xl:grid-cols-3"
        collapsedClassName={`${facilities?.length >= 12 ? "h-[180px]" : ""}`}
        content={getElements(facilities)}
        lessText="بستن"
        moreText="مشاهده همه امکانات"
        eventKey={
          process.env.NEXT_PUBLIC_APP_NAME === "villa"
            ? USER_TRACKING_EVENTS.VILLA_AMENITIES_MODAL_OPEN
            : undefined
        }
        title={`امکانات ${hotelType}`}
        titleStyle="text-subtitle-1 md:text-headline-6"
        hasCustomBehaviourButton={
          Object.entries(facilitiesGroup || {}).length > 0
        }
        onButtonClick={
          Object.entries(facilitiesGroup || {}).length > 0
            ? () => setIsModalOpen((previous) => !previous)
            : undefined
        }
      />
      {facilitiesGroup && (
        <Modal
          dialogClassName="lg:!h-[640px] lg:!w-[720px] overflow-y-scroll rounded-xl  md:rounded-0 !h-[90vh] md:!h-full !py-3"
          headerStyle="!h-16 "
          onClose={() => setIsModalOpen(false)}
          open={isModalOpen}
          showCloseButton={false}
          titleClassName="line-clamp-2"
          usePortal
        >
          <div className="flex items-center justify-between pb-2">
            <div className="flex flex-col gap-1">
              <h6 className="text-subtitle-1">امکانات اقامتگاه</h6>
              <p className="text-extra-caption text-on-surface-high-emphasis">
                {hotelTitle}
              </p>
            </div>
            <div>
              <Button
                variant="text"
                color="neutral"
                size="x-small"
                className="!p-0"
                onClick={() => setIsModalOpen(false)}
              >
                <MaterialIcon className="!mx-0 text-[24px]" name="close" />
              </Button>
            </div>
          </div>
          <div
            ref={reference}
            onScroll={onScroll}
            className="no-scrollbar flex-1 overflow-auto md:border-y"
          >
            {Object.entries(facilitiesGroup).map(([title, items], index) => (
              <div
                className={`${
                  index === Object.entries(facilitiesGroup).length - 1
                    ? ""
                    : "border-b"
                } py-4`}
                key={index}
              >
                <p className="text-subtitle-1">{title}</p>
                <div className="flex w-full flex-col flex-wrap md:flex-row">
                  {items.map((item) => (
                    <div
                      className="flex w-full items-center gap-4 py-4 md:w-1/2 md:flex-row md:py-2"
                      key={`${item.title}-${index}`}
                    >
                      <MaterialIcon
                        className="text-on-surface-medium-emphasis text-xl"
                        name={
                          (item.icon as MaterialIconPropertiesType["name"]) ||
                          "check_box"
                        }
                        variant="outlined"
                      />
                      <div className="flex w-[98%] flex-col">
                        <span className="text-subtitle-2">
                          {upperFirst(
                            numberConvert(item.title, { locale: router.locale })
                          )}
                        </span>
                        {item?.description && (
                          <span className="text-body-2">
                            {numberConvert(item?.description, {
                              locale: router.locale
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button
              className="mt-4 w-full sm:w-[100px]"
              color="neutral"
              onClick={() => setIsModalOpen(false)}
              text="بستن"
              variant="outline"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};
export const ReserveFacilities = memo(Component, deepCompare);
